'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from '@fshr/context/i18n-context';
import { IconCloseAlt, IconExclusiveAmenities, IconExclusiveAmenitiesFilled } from '../icon';

export interface ItinerarySurveyCardProps {
	createSurveyLink: (starRating: number) => string;
	onSurveyInteraction: () => void;
	onDismiss: () => void;
	className?: string;
}

export const ItinerarySurveyCard = ({
	createSurveyLink,
	onSurveyInteraction,
	onDismiss,
	className,
}: ItinerarySurveyCardProps) => {
	const t = useTranslations();
	const [selectedRating, setSelectedRating] = useState<number | null>(null);

	useEffect(() => {
		if (selectedRating === null) return;

		localStorage.setItem('starRating', selectedRating.toString());

		const surveyWidth = 930;
		const surveyHeight = 740;

		//500ms delay before opening survey
		const openSurveyTimeout = setTimeout(() => {
			const surveyUrl = createSurveyLink(selectedRating);
			window.open(
				surveyUrl,
				'_blank',
				`width=${surveyWidth},height=${surveyHeight},top=${
					(window.outerHeight - surveyHeight) / 2
				},left=${(window.outerWidth - surveyWidth) / 2}`
			);
		}, 500);

		//2000ms delay before hiding card
		const dismissTimeout = setTimeout(() => {
			onSurveyInteraction();
		}, 2000);

		return () => {
			localStorage.removeItem('starRating');
			clearTimeout(openSurveyTimeout);
			clearTimeout(dismissTimeout);
		};
	}, [selectedRating, createSurveyLink, onSurveyInteraction]);

	const stopNativePropagation = (nativeEvent: Event) => {
		if (typeof nativeEvent?.stopImmediatePropagation === 'function') {
			nativeEvent.stopImmediatePropagation();
		}
	};

	const handleStarRating = (e: React.MouseEvent<HTMLButtonElement>, rating: number) => {
		e.preventDefault();
		e.stopPropagation();
		stopNativePropagation(e.nativeEvent);
		setSelectedRating(rating);
	};

	const handleDismiss = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		stopNativePropagation(e.nativeEvent);
		onDismiss();
	};

	return (
		<div
			className="flex items-stretch spacing-inline-xs relative ml-[1px] md:ml-0"
			role="complementary"
			aria-label={t('Itinerary survey prompt')}
		>
			{/* Line Container */}
			<div className="h-full w-2.5 flex flex-col">
				<div className="absolute top-0 bottom-0 left-[6px]" aria-hidden={true}>
					{/* Line */}
					<div className="w-[1px] h-full bg-[rgb(77,77,77,0.45)]" />
				</div>
			</div>

			<div className="flex-1 pb-200">
				{/* Card */}
				<div className="flex bg-surface-raised-subtle justify-between items-start relative rounded bg-white border border-border-subtle spacing-card-padding-xs">
					<div className="flex flex-col justify-between spacing-stack-s">
						<p className="text-caption">
							{t('How satisfied are you with your itinerary experience today?')}
						</p>

						<div className="flex items-center">
							<div
								className="flex gap-100"
								role="radiogroup"
								aria-label={t('Rate your itinerary experience')}
							>
								{[1, 2, 3, 4, 5].map(starNumber => (
									<button
										type="button"
										key={`star-${starNumber}`}
										onClick={e => handleStarRating(e, starNumber)}
										aria-label={t('Rate {{stars}} stars', [starNumber.toString()])}
									>
										{selectedRating && selectedRating > starNumber - 1 ? (
											<IconExclusiveAmenitiesFilled width={24} height={24} />
										) : (
											<IconExclusiveAmenities width={24} height={24} />
										)}
									</button>
								))}
							</div>
						</div>
					</div>
					<button
						type="button"
						onClick={handleDismiss}
						className="text-text-secondary hover:text-text-heading"
						aria-label={t('Dismiss survey')}
					>
						<IconCloseAlt width={22} height={22} />
					</button>
				</div>
			</div>
		</div>
	);
};

export default ItinerarySurveyCard;
